import React from 'react';
import ConstituentQueryTypeFields from '@/constants/constituents/ConstituentQueryTypeFields';
import createApiRequest from '@/lib/axios/createApiRequest';
import useCurrentUser from '@/lib/hooks/useCurrentUser';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import type ConstituentExport from '@/types/api/ConstituentExport';
import type ConstituentExportField from '@/types/api/ConstituentExportField';
import type ConstituentExportFilters from '@/types/api/ConstituentExportFilters';

const useConstituentExport = () => {
  const ownerOrgId = useOwnerOrgId();
  const { user } = useCurrentUser();
  const [apiResponse, setApiResponse] = React.useState<{
    res: null | APIResponse<{ constituentExport: ConstituentExport }>;
    error: null | Error | APIResponse<{ constituentExport: ConstituentExport }>;
  }>({
    res: null,
    error: null,
  });
  const [isExporting, setIsExporting] = React.useState(false);

  const createExport = React.useCallback(
    async ({
      fields = [],
      filters,
      path = 'constituent-exports',
      ...rest
    }: {
      fields?: ConstituentExportField[];
      filters: ConstituentExportFilters;
      path?: string;
    }) => {
      if (isExporting) return;

      setIsExporting(true);
      const trueFields = fields
        ?.filter((field) => field.value === true)
        .sort((field) => (field.fieldName === 'primary_email' ? -1 : 1));

      const { filters: searchFilters = [], advancedFilters = [] } = filters;

      try {
        const res = await createApiRequest<{
          constituentExport: ConstituentExport;
        }>({
          method: 'post',
          path,
          data: {
            createdBy: {
              userId: user.userId,
              displayName: user.displayName,
            },
            fields: trueFields,
            ownerOrgId,
            searchParams: {
              filters: [...searchFilters, ...advancedFilters],
              queryType: ConstituentQueryTypeFields.V4,
            },
            ...rest,
          },
          throwErrors: true,
        });

        if (res) {
          setApiResponse({ res, error: null });
        } else {
          throw new Error(`error with api request, /${path}`);
        }
      } catch (e) {
        // The error functionality of createApiRequest is expecting a very
        // specific shape of the error response. However, not all errors will
        // come from our API code (such as a network issue) so using this
        // fallback to set an error if none exists.
        if (!e) {
          setApiResponse({
            res: null,
            error: new Error(`error with api request, /${path}`),
          });
        } else {
          setApiResponse({ res: null, error: e } as {
            res: null;
            error: APIResponse<{ constituentExport: ConstituentExport }>;
          });
        }
      } finally {
        setIsExporting(false);
      }
    },
    [isExporting, ownerOrgId, user],
  );

  return {
    apiResponse,
    createExport,
    isExporting,
    resetApiResponse() {
      setApiResponse({ error: null, res: null });
    },
  };
};

export default useConstituentExport;
